import React, {useMemo} from "react";
import {ChevronRightIcon} from "@heroicons/react/24/outline";
import "../userAccountAssetList.css";

const INDEX_COL_WIDTH = 80;

const TABLE_COLUMNS = [
    {label: "No.", field: "_index", width: INDEX_COL_WIDTH},
    {label: "자산명", field: "assetName", minWidth: 140, primary: true},
    {label: "자산코드", field: "assetCode", minWidth: 110},
    {label: "자산그룹", field: "groupName", minWidth: 96},
    {label: "업무그룹", field: "taskName", minWidth: 96},
    {label: "자산위치", field: "locationName", minWidth: 96},
    {label: "부서", field: "departmentName", minWidth: 88},
    {label: "시스템", field: "projectName", minWidth: 88},
];

const ACTION_COL_WIDTH = 28;

function displayValue(value) {
    if (value == null || value === "") {
        return "—";
    }
    return String(value);
}

function buildGridTemplate() {
    const cols = TABLE_COLUMNS.map((col) => {
        if (col.width != null) {
            return `${col.width}px`;
        }
        return `minmax(${col.minWidth}px, 1fr)`;
    }).join(" ");
    return `${cols} ${ACTION_COL_WIDTH}px`;
}

function buildMinWidth() {
    return TABLE_COLUMNS.reduce((sum, col) => sum + (col.width ?? col.minWidth), 0) + ACTION_COL_WIDTH;
}

function UserAccountAssetPagination({pageable, onPageChange}) {
    const pageNumber = Number(pageable?.pageNumber ?? 0);
    const totalPages = Math.max(1, Number(pageable?.totalPages ?? 1));
    const totalElements = Number(pageable?.totalElements ?? 0);
    const isFirst = pageNumber <= 0;
    const isLast = pageNumber >= totalPages - 1;

    const changePage = (next) => {
        if (!onPageChange || next < 0 || next >= totalPages) {
            return;
        }
        onPageChange({
            ...pageable,
            pageNumber: next,
        });
    };

    return (
        <div className="user-account-asset-list__pagination">
            <span className="user-account-asset-list__pagination-summary">
                총 <strong>{totalElements.toLocaleString()}</strong>건 ·{" "}
                <strong>{pageNumber + 1}</strong> / {totalPages} 페이지
            </span>
            <div className="user-account-asset-list__pagination-actions">
                <button
                    type="button"
                    className="user-account-asset-list__page-btn"
                    disabled={isFirst}
                    onClick={() => changePage(pageNumber - 1)}
                >
                    이전
                </button>
                <button
                    type="button"
                    className="user-account-asset-list__page-btn"
                    disabled={isLast}
                    onClick={() => changePage(pageNumber + 1)}
                >
                    다음
                </button>
            </div>
        </div>
    );
}

function UserAccountAssetList({newData, onPageChange, onRowClick, className = ""}) {
    const rows = newData?.content ?? [];
    const gridTemplate = useMemo(() => buildGridTemplate(), []);
    const minWidth = useMemo(() => buildMinWidth(), []);

    const pageable = useMemo(() => ({
        ...(newData?.pageable ?? {}),
        totalPages: newData?.totalPages ?? newData?.pageable?.totalPages ?? 1,
        totalElements: newData?.totalElements ?? newData?.pageable?.totalElements ?? 0,
        pageSize: newData?.pageable?.pageSize ?? newData?.pageable?.size ?? 15,
        size: newData?.pageable?.pageSize ?? newData?.pageable?.size ?? 15,
    }), [newData]);

    const pageNumber = Number(pageable.pageNumber ?? 0);
    const pageSize = Number(pageable.pageSize ?? pageable.size ?? 15);
    const rowOffset = pageNumber * pageSize;

    return (
        <div className={`user-account-asset-list ${className}`.trim()}>
            <div className="user-account-asset-list__body">
                <div className="user-account-asset-list__scroll">
                    {rows.length === 0 ? (
                        <div className="user-account-asset-list__empty" role="status">
                            조회된 자산이 없습니다.
                        </div>
                    ) : (
                        <div className="user-account-asset-list__table-wrap">
                            <div
                                className="user-account-asset-list__table"
                                style={{minWidth}}
                                role="table"
                            >
                                <div
                                    className="user-account-asset-list__thead"
                                    style={{gridTemplateColumns: gridTemplate}}
                                    role="row"
                                >
                                    {TABLE_COLUMNS.map((col) => (
                                        <span
                                            key={col.field}
                                            className={`user-account-asset-list__th${
                                                col.primary ? " is-primary" : ""
                                            }${col.field === "_index" ? " is-index" : ""}`}
                                            role="columnheader"
                                        >
                                            {col.label}
                                        </span>
                                    ))}
                                    <span className="user-account-asset-list__th is-action" aria-hidden />
                                </div>

                                <div className="user-account-asset-list__tbody" role="rowgroup">
                                    {rows.map((row, rowIndex) => (
                                        <button
                                            key={row.id}
                                            type="button"
                                            className="user-account-asset-list__tr"
                                            style={{gridTemplateColumns: gridTemplate}}
                                            role="row"
                                            onClick={() => onRowClick?.(row)}
                                        >
                                            {TABLE_COLUMNS.map((col) => (
                                                <span
                                                    key={col.field}
                                                    className={`user-account-asset-list__td${
                                                        col.primary ? " is-primary" : ""
                                                    }${col.field === "_index" ? " is-index" : ""}`}
                                                    role="cell"
                                                >
                                                    {col.field === "_index"
                                                        ? rowOffset + rowIndex + 1
                                                        : displayValue(row[col.field])}
                                                </span>
                                            ))}
                                            <span className="user-account-asset-list__td is-action" aria-hidden>
                                                <ChevronRightIcon className="user-account-asset-list__chevron" />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <UserAccountAssetPagination pageable={pageable} onPageChange={onPageChange} />
            </div>
        </div>
    );
}

export default UserAccountAssetList;
